import { useEffect, useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import swal from "sweetalert";
import { aiService } from "../../service/api.service";
import { InlineLoader, SkeletonLine } from "../loader";

function AssignNumberModal({ show, handleClose, agent, onSuccess }) {
  const [numbers, setNumbers] = useState([]);
  const [numbersLoading, setNumbersLoading] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!show || !agent) return;
    setSelectedPhone("");
    setNumbersLoading(true);
    aiService.getAvailableNumbers()
      .then((res) => setNumbers(res?.data?.data || []))
      .catch(() => setNumbers([]))
      .finally(() => setNumbersLoading(false));
  }, [show, agent]);

  const resetAndClose = () => {
    setNumbers([]);
    setSelectedPhone("");
    setAssigning(false);
    setDeleting(false);
    handleClose();
  };

  const handleAssign = async () => {
    if (!selectedPhone) return;
    setAssigning(true);
    try {
      await aiService.assignNumber({ agent: agent._id, phone: selectedPhone });
      swal({ icon: "success", text: "Phone number assigned successfully." });
      onSuccess && onSuccess();
      resetAndClose();
    } catch (error) {
      setAssigning(false);
      if (error?.response?.status === 401) {
        swal({ icon: "error", text: "Unauthorized", button: "OK" }).then(() => {
          localStorage.clear();
          window.location.href = "/";
        });
      } else {
        swal({ icon: "error", text: error?.response?.data?.message || "Error assigning phone number." });
      }
    }
  };

  const handleDelete = () => {
    swal({
      title: "Confirmation",
      text: `Are you sure you want to delete the number "${agent?.phone}"?`,
      icon: "warning",
      buttons: ["NO", "YES"],
    }).then(async (val) => {
      if (!val) return;
      setDeleting(true);
      try {
        await aiService.deleteNumber(agent.phone_id);
        swal({ icon: "success", text: "Phone number deleted successfully." });
        onSuccess && onSuccess();
        resetAndClose();
      } catch (error) {
        setDeleting(false);
        if (error?.response?.status === 401) {
          swal({ icon: "error", text: "Unauthorized", button: "OK" }).then(() => {
            localStorage.clear();
            window.location.href = "/";
          });
        } else {
          swal({ icon: "error", text: error?.response?.data?.message || "Error deleting phone number." });
        }
      }
    });
  };

  return (
    <Modal show={show} onHide={resetAndClose} className="premium-modal" centered>
      <Modal.Header closeButton>
        <Modal.Title>Phone Number{agent?.name ? ` - ${agent.name}` : ""}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {agent?.phone && (
          <Form.Group className="mb-3">
            <Form.Label>Assigned Number</Form.Label>
            <Form.Control value={agent.phone} disabled readOnly />
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>{agent?.phone ? "Assign a different number" : "Available Numbers"}</Form.Label>
          {numbersLoading ? (
            <SkeletonLine width="100%" height={80} />
          ) : numbers.length > 0 ? (
            <div className="agent-user-select-list">
              {numbers.map((n) => (
                <Form.Check
                  key={n.phone}
                  type="radio"
                  name="assignPhoneNumber"
                  id={`phone-${n.phone}`}
                  label={n.phone}
                  checked={selectedPhone === n.phone}
                  onChange={() => setSelectedPhone(n.phone)}
                  className="mb-2"
                />
              ))}
            </div>
          ) : (
            <p className="text-muted mb-0">No phone numbers available.</p>
          )}
        </Form.Group>

        <div className="d-flex gap-2">
          <Button
            variant="primary"
            type="button"
            disabled={!selectedPhone || assigning || deleting}
            onClick={handleAssign}
          >
            {assigning ? <InlineLoader /> : "Assign Number"}
          </Button>
          {agent?.phone && (
            <Button
              variant="danger"
              type="button"
              disabled={assigning || deleting}
              onClick={handleDelete}
            >
              {deleting ? <InlineLoader /> : "Delete Number"}
            </Button>
          )}
          <Button variant="secondary" type="button" disabled={assigning || deleting} onClick={resetAndClose}>
            Cancel
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default AssignNumberModal;
